import { useRef, useState } from 'react';
import { Combobox, Option } from '@fluentui/react-components';
import { useApi } from '../../api/useApi';
import type { DirectoryUserDto } from '../../api/types';
import './UserPicker.css';

interface UserPickerProps {
  onSelect: (user: DirectoryUserDto | undefined) => void;
  placeholder?: string;
  /** Nach der Auswahl die Eingabe leeren (für Mehrfach-Hinzufügen). */
  clearOnSelect?: boolean;
  /** Füllt die volle Breite des Containers und nutzt die größere Variante. */
  fluid?: boolean;
  /** Nur Gastkonten listen (für das Gäste-Feld der Team-Bearbeitung). */
  guestsOnly?: boolean;
}

/** Wiederverwendbare Personensuche (Combobox) gegen /api/users/search. */
export function UserPicker({ onSelect, placeholder, clearOnSelect, fluid, guestsOnly }: UserPickerProps) {
  const api = useApi();
  const [text, setText] = useState('');
  const [results, setResults] = useState<DirectoryUserDto[]>([]);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const timer = useRef<number | undefined>(undefined);

  function onInput(value: string) {
    setText(value);
    setSelectedId(undefined);
    onSelect(undefined);
    if (timer.current) window.clearTimeout(timer.current);
    if (value.trim().length < 2) {
      setResults([]);
      return;
    }
    timer.current = window.setTimeout(() => {
      api
        .searchUsers(value.trim(), guestsOnly)
        .then(setResults)
        .catch(() => setResults([]));
    }, 300);
  }

  return (
    <Combobox
      freeform
      className={fluid ? 'userpicker-cb-fluid' : undefined}
      size={fluid ? 'large' : undefined}
      placeholder={placeholder ?? 'Person suchen…'}
      value={text}
      selectedOptions={selectedId ? [selectedId] : []}
      onInput={(ev) => onInput((ev.target as HTMLInputElement).value)}
      onOptionSelect={(_, d) => {
        const u = results.find((r) => r.id === d.optionValue);
        if (!u) return;
        onSelect(u);
        if (clearOnSelect) {
          setText('');
          setSelectedId(undefined);
          setResults([]);
        } else {
          setSelectedId(u.id);
          setText(u.displayName);
        }
      }}
    >
      {results.map((u) => (
        <Option key={u.id} value={u.id} text={u.displayName}>
          <span className="userpicker-opt">
            <span className="userpicker-opt-name pii">{u.displayName}</span>
            <span className="userpicker-opt-upn pii">{u.userPrincipalName}</span>
          </span>
        </Option>
      ))}
    </Combobox>
  );
}
